<template>
    <v-item-group v-model="inputValue" :class="classList" :multiple="multiple">
        <v-layout fill-height>
            <v-flex
                v-for="(item, itemIndex) in itemList"
                :key="itemIndex"
                class="d-flex justify-center app-item-group__col"
            >
                <v-item v-slot="{ active, toggle }" :value="item.id" :disabled="disabled || item.disabled">
                    <button
                        v-ripple="!disabled && !item.disabled"
                        type="button"
                        :disabled="disabled || item.disabled"
                        :class="[
                            'app-item-group__item rounded',
                            {
                                'app-item-group__item--active': active,
                                'app-item-group__item--disabled': disabled || item.disabled
                            }
                        ]"
                        color="inherit"
                        @click="toggle"
                    >
                        <v-card-text class="app-item-group__content">
                            <h3 v-if="item.heading" class="app-item-group__heading">{{ item.heading }}</h3>

                            <div v-if="Array.isArray(item.text)" class="my-2">
                                <p
                                    v-for="(itemTextLine, index) in item.text"
                                    :key="index"
                                    class="app-item-group__text mb-1"
                                >
                                    {{ itemTextLine }}
                                </p>
                            </div>
                            <p v-else-if="item.text" class="app-item-group__text mt-2 mb-3">{{ item.text }}</p>

                            <v-icon v-if="item.icon" class="app-item-group__icon mt-2 mb-4">
                                {{ getIcon(item.icon) }}
                            </v-icon>

                            <template v-if="item.disabled && !dense">
                                <div class="font-weight-bold">
                                    {{ disabledItemText ? disabledItemText : $t('disabled') }}
                                </div>
                            </template>

                            <app-button
                                v-else-if="!dense"
                                depressed
                                tabindex="-1"
                                :disabled="disabled || item.disabled"
                                :secondary="item.disabled"
                            >
                                {{ isActive(item, itemIndex) ? selectedText : selectText }}
                                <v-slide-y-transition hide-on-leave>
                                    <v-icon v-if="isActive(item, itemIndex)" right dense>
                                        {{ getIcon('success', 'fad') }}
                                    </v-icon>
                                </v-slide-y-transition>
                            </app-button>

                            <app-button
                                v-else-if="dense"
                                depressed
                                tabindex="-1"
                                :disabled="disabled || item.disabled"
                                :secondary="item.disabled"
                                small
                                icon
                            >
                                <v-slide-y-transition hide-on-leave>
                                    <v-progress-circular
                                        v-if="items[itemIndex].loading"
                                        indeterminate
                                        :size="16"
                                        :width="2"
                                        color="primaryLight"
                                    />
                                </v-slide-y-transition>
                                <v-slide-y-transition hide-on-leave>
                                    <v-icon
                                        v-if="!items[itemIndex].loading && isActive(item, itemIndex)"
                                        small
                                        dense
                                        color="primaryLight"
                                    >
                                        {{ getIcon('success', 'fad') }}
                                    </v-icon>
                                </v-slide-y-transition>
                                <v-slide-y-transition hide-on-leave>
                                    <v-icon v-if="!items[itemIndex].loading && !isActive(item, itemIndex)" small dense>
                                        {{ getIcon('radioOff', 'far') }}
                                    </v-icon>
                                </v-slide-y-transition>
                            </app-button>
                        </v-card-text>
                    </button>
                </v-item>
            </v-flex>
        </v-layout>
    </v-item-group>
</template>

<script>
export default {
    name: 'AppItemGroup',
    props: {
        value: {
            type: [Boolean, String, Number, Array],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        disabledItemText: {
            type: String,
            default: null
        },
        required: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        },
        items: {
            type: Array,
            required: true,
            validator: items => {
                if (!Array.isArray(items)) return false;
                return items.every(item => item.heading || item.text || item.icon);
            }
        },
        multiple: {
            type: Boolean,
            default: false
        },
        selectText: {
            type: String,
            default: 'Select'
        },
        selectedText: {
            type: String,
            default: 'Selected'
        }
    },
    computed: {
        inputValue: {
            get() {
                return this.value;
            },
            set(v) {
                this.$emit('input', v);
            }
        },
        itemList() {
            return this.items.map(item => {
                return {
                    id: null,
                    heading: null,
                    text: null,
                    icon: null,
                    disabled: false,
                    ...item
                };
            });
        },
        classList() {
            let classes = ['app-item-group'];

            if (this.dense) {
                classes.push('app-item-group--dense');
            }

            return classes.join(' ');
        }
    },
    methods: {
        isActive(item, itemIndex) {
            if (Array.isArray(this.value)) {
                if (typeof item === 'object' && 'id' in item) {
                    return this.value.includes(item.id);
                }

                return this.value.includes(itemIndex);
            }

            if (typeof item === 'object' && 'id' in item) {
                return item.id === this.value;
            }

            return itemIndex === this.value;
        }
    }
};
</script>

<style lang="scss">
.app-item-group {
    &__col {
        flex-basis: 100%;
        padding: 0 0.7rem;
    }

    &__item {
        height: 100%;
        width: 100%;
        flex-basis: 100%;
        transition: all 0.15s ease-in-out;
        background: var(--v-primaryLight-base);
        color: var(--v-primaryText-base);
        text-align: inherit;

        &--active {
            background: var(--v-primaryText-base);
            color: var(--v-primaryLight-base);
        }

        &--disabled {
            cursor: default;
            background: none;
            color: var(--v-primaryText-base);
        }

        &:focus-visible {
            outline: 1px solid var(--v-primaryText-base);
        }
    }
    &__content {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        color: inherit;
        text-align: inherit;
        padding: 0.7rem 1rem;
    }
    &__heading {
        line-height: 1.3 !important;
        font-weight: bold !important;
        color: inherit !important;
        font-size: 1.5rem;
    }
    &__text {
        color: inherit;
        line-height: 1.4;
    }
    &__icon {
        color: inherit !important;
        font-size: 7rem !important;
    }

    &--dense {
        .app-item-group__col {
            padding: 0 0.3rem;
        }
        .app-item-group__content {
            padding: 0.4rem 0.7rem;
            display: flex;
            flex-direction: row;
            align-items: center;
        }
        .app-item-group__heading {
            font-size: 1rem;
        }
    }
}
</style>
